import React, { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Shield, Smartphone, Mail, Loader2, QrCode, Copy, CheckCircle, Download } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { TwoFactorService } from '../../lib/auth/twoFactor';
import { useAuthStore } from '../../stores/authStore';

interface TwoFactorAuthProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export function TwoFactorAuth({ isOpen, onClose, onComplete }: TwoFactorAuthProps) {
  const { user } = useAuthStore();
  const [step, setStep] = useState<'setup' | 'verify' | 'backup'>('setup');
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState<{
    secret: string;
    qrCodeUrl: string;
    backupCodes: string[];
  } | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      initializeSetup();
    }
  }, [isOpen, user]);

  const initializeSetup = async () => {
    if (!user?.email) {
      toast.error('User email not found');
      onClose();
      return;
    }
    
    setLoading(true);
    try {
      const data = await TwoFactorService.setupTwoFactor(user.id, user.email);
      setSetupData(data);
    } catch (error) {
      console.error('Failed to initialize 2FA:', error);
      toast.error('Failed to initialize 2FA setup');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setLoading(true);
    try {
      const isValid = await TwoFactorService.verifyToken(user.id, verificationCode);
      
      if (isValid) {
        await TwoFactorService.enableTwoFactor(user.id);
        setStep('backup');
      } else {
        toast.error('Invalid verification code');
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast.error('Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    onComplete();
    onClose();
    toast.success('Two-factor authentication enabled successfully');
  };

  const copySecretKey = () => {
    if (setupData?.secret) {
      navigator.clipboard.writeText(setupData.secret);
      toast.success('Secret key copied to clipboard');
    }
  };

  const downloadBackupCodes = () => {
    if (!setupData?.backupCodes) return;

    const content = `BIDZORA 2FA BACKUP CODES\n\n${setupData.backupCodes.join('\n')}\n\nKeep these codes safe and secure. Each code can only be used once.`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bidzora-2fa-backup-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Set Up Two-Factor Authentication"
    >
      <div className="space-y-6">
        <div className="flex items-start space-x-3">
          <div className="rounded-lg bg-indigo-100 p-2">
            <Shield className="h-6 w-6 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-base font-medium text-gray-900">
              Enhance Your Account Security
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Two-factor authentication adds an extra layer of security to your account
            </p>
          </div>
        </div>

        {step === 'setup' && setupData && (
          <div className="space-y-6">
            <div className="rounded-lg bg-gray-50 p-4">
              <h4 className="text-sm font-medium text-gray-900">1. Install an authenticator app</h4>
              <p className="mt-1 text-sm text-gray-500">
                Download and install Google Authenticator or Authy on your mobile device
              </p>
            </div>

            <div className="rounded-lg bg-gray-50 p-4">
              <h4 className="text-sm font-medium text-gray-900">2. Scan QR code or enter key manually</h4>
              <div className="mt-4 flex items-center justify-center">
                <img
                  src={setupData.qrCodeUrl}
                  alt="QR Code"
                  className="h-48 w-48 rounded-lg border border-gray-200"
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">
                  Secret Key
                </label>
                <div className="mt-1 flex items-center space-x-2">
                  <code className="flex-1 rounded-md bg-gray-100 px-3 py-2 text-sm font-mono">
                    {setupData.secret}
                  </code>
                  <button
                    onClick={copySecretKey}
                    className="rounded-md border border-gray-300 p-2 hover:bg-gray-50"
                    title="Copy secret key"
                  >
                    <Copy className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setStep('verify')}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 'verify' && (
          <form onSubmit={handleVerification} className="space-y-6">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                Enter Verification Code
              </label>
              <p className="mt-1 text-sm text-gray-500">
                Enter the 6-digit code from your authenticator app
              </p>
              <div className="mt-4">
                <input
                  type="text"
                  id="code"
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-center text-2xl tracking-widest shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  placeholder="000000"
                  required
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setStep('setup')}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify'
                )}
              </button>
            </div>
          </form>
        )}

        {step === 'backup' && setupData && (
          <div className="space-y-6">
            <div className="rounded-lg bg-yellow-50 p-4">
              <h4 className="text-sm font-medium text-yellow-900">Save Your Backup Codes</h4>
              <p className="mt-1 text-sm text-yellow-700">
                Keep these backup codes in a safe place. You can use them to access your account if you lose your authenticator device.
              </p>
            </div>

            <div className="rounded-lg border-2 border-dashed border-gray-300 p-4">
              <div className="grid grid-cols-2 gap-4">
                {setupData.backupCodes.map((code, index) => (
                  <div key={index} className="font-mono text-sm">{code}</div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={downloadBackupCodes}
                className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <Download className="mr-2 h-4 w-4" />
                Download Codes
              </button>
              <button
                onClick={handleComplete}
                className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Complete Setup
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}